import { useEffect, useMemo, useRef, useState } from "react";
import { MdClose, MdRefresh, MdSend } from "react-icons/md";
import { sendChatMessage } from "../lib/api";

const QUICK = [
  { id: "q1", label: "보험금 사례 알아볼래 ✨" },
  { id: "q2", label: "암보험 알아볼래 🍀" },
  { id: "q3", label: "지금 보험 문제없을까 🤔" },
  { id: "q4", label: "나에게 맞는 보험은 ❓" },
];

function nowTime() {
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function getInitialMessages() {
  return [
    {
      id: "m1",
      role: "bot",
      text: "안녕하세요. 굿리치 플러스 상담 도우미입니다.\n어떤 점이 가장 궁금하신가요?",
      time: nowTime(),
    },
    {
      id: "m2",
      role: "bot",
      text: "예) 치료비 부담, 보장 구성, 보험료 수준, 가입 시점 등",
      time: nowTime(),
    },
  ];
}

export default function ChatbotPopupPage() {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState(getInitialMessages);
  const [isLoading, setIsLoading] = useState(false);

  const sessionIdRef = useRef(crypto.randomUUID());
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);

  const headerTitle = "굿리치 플러스 상담";

  const hero = useMemo(
    () => ({
      title: "상담을 더 빠르게",
      desc: "간단한 질문만으로 필요한 보장 방향을 빠르게 안내해드립니다.",
    }),
    [],
  );

  const scrollToBottom = (smooth = true) => {
    bottomRef.current?.scrollIntoView({
      behavior: smooth ? "smooth" : "auto",
      block: "end",
    });
  };

  const checkIsNearBottom = () => {
    const el = scrollRef.current;
    if (!el) return true;

    const threshold = 100;
    return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
  };

  const handleScroll = () => {
    shouldAutoScrollRef.current = checkIsNearBottom();
  };

  useEffect(() => {
    scrollToBottom(false);
  }, []);

  useEffect(() => {
    if (shouldAutoScrollRef.current) {
      scrollToBottom(messages.length > 2);
    }
  }, [messages, isLoading]);

  const pushBot = (text) => {
    setMessages((prev) => [
      ...prev,
      { id: crypto.randomUUID(), role: "bot", text, time: nowTime() },
    ]);
  };

  const pushUser = (text) => {
    setMessages((prev) => [
      ...prev,
      { id: crypto.randomUUID(), role: "user", text, time: nowTime() },
    ]);
  };

  const sendMessageToChatApi = async (text) => {
    try {
      setIsLoading(true);

      const data = await sendChatMessage({
        message: text,
        sessionId: sessionIdRef.current,
      });

      pushBot(data?.message || "응답이 없습니다.");
    } catch (error) {
      console.error(error);
      pushBot(
        "현재 상담 연결이 원활하지 않습니다.\n잠시 후 다시 시도해주세요.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    const v = input.trim();
    if (!v || isLoading) return;

    shouldAutoScrollRef.current = true;
    pushUser(v);
    setInput("");

    requestAnimationFrame(() => {
      scrollToBottom();
    });

    await sendMessageToChatApi(v);
  };

  const handleQuick = async (label) => {
    if (isLoading) return;

    shouldAutoScrollRef.current = true;
    pushUser(label);

    requestAnimationFrame(() => {
      scrollToBottom();
    });

    await sendMessageToChatApi(label);
  };

  const handleReset = () => {
    sessionIdRef.current = crypto.randomUUID();
    shouldAutoScrollRef.current = true;
    setMessages(getInitialMessages());
    setInput("");
    setIsLoading(false);

    requestAnimationFrame(() => {
      scrollToBottom(false);
    });
  };

  const handleCallPlanner = () => {
    window.location.href = "tel:15663611";
  };

  return (
    <div className="h-screen w-screen bg-white">
      <div className="sticky top-0 z-20 border-b bg-white/90 backdrop-blur">
        <div className="flex h-14 items-center justify-between px-4">
          <button
            type="button"
            onClick={() => window.close()}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl hover:bg-gray-100"
          >
            <MdClose className="text-2xl text-secondary" />
          </button>

          <div className="text-center">
            <p className="font-extrabold text-secondary">{headerTitle}</p>
            <p className="text-sm text-gray-500">AI 챗봇</p>
          </div>

          <button
            type="button"
            onClick={handleReset}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl hover:bg-gray-100"
          >
            <MdRefresh className="text-2xl text-secondary" />
          </button>
        </div>
      </div>

      <div className="mx-auto flex h-[calc(100vh-56px)] max-w-[520px] flex-col">
        <div className="px-4 pt-4">
          <div className="rounded-2xl border bg-white p-4 shadow-sm">
            <p className="text-sm font-extrabold text-secondary">
              {hero.title}
            </p>
            <p className="mt-1 text-sm text-gray-600">{hero.desc}</p>

            <div className="mt-3 flex flex-wrap gap-2">
              {QUICK.map((q) => (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => handleQuick(q.label)}
                  disabled={isLoading}
                  className="rounded-full bg-secondary/5 px-3 py-2 text-sm font-semibold text-secondary hover:bg-secondary/10 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {q.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-4 py-4"
        >
          <div className="space-y-3">
            {messages.map((m) => {
              const isUser = m.role === "user";

              return (
                <div
                  key={m.id}
                  className={`flex ${isUser ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={[
                      "max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm",
                      isUser
                        ? "bg-main text-white"
                        : "bg-gray-100 text-secondary",
                    ].join(" ")}
                  >
                    <pre className="whitespace-pre-wrap font-sans">
                      {m.text}
                    </pre>
                    <p
                      className={[
                        "mt-1 text-[10px]",
                        isUser ? "text-white/80" : "text-gray-500",
                      ].join(" ")}
                    >
                      {m.time}
                    </p>
                  </div>
                </div>
              );
            })}

            {isLoading && (
              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-2xl bg-gray-100 px-5 py-4 text-secondary shadow-sm">
                  <div className="flex items-center gap-2">
                    <div className="flex items-end gap-1">
                      <span className="h-1.5 w-1.5 animate-[bounce_0.6s_infinite] rounded-full bg-secondary/80 [animation-delay:-0.3s]" />
                      <span className="h-1.5 w-1.5 animate-[bounce_0.6s_infinite] rounded-full bg-secondary/70 [animation-delay:-0.15s]" />
                      <span className="h-1.5 w-1.5 animate-[bounce_0.6s_infinite] rounded-full bg-secondary/80" />
                    </div>
                  </div>
                  <p className="mt-2 text-[10px] text-gray-500">{nowTime()}</p>
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>
        </div>

        <div className="sticky bottom-0 border-t bg-white px-3 py-3">
          <div className="flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                  handleSend();
                }
              }}
              placeholder="무엇이든 물어보세요"
              disabled={isLoading}
              className="h-12 flex-1 rounded-xl border border-gray-200 px-4 text-sm outline-none disabled:bg-gray-50"
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={isLoading || !input.trim()}
              className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-main text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              <MdSend className="text-2xl" />
            </button>
          </div>

          <div className="mt-2 flex items-center justify-between gap-2">
            <p className="text-[11px] text-gray-500">
              AI 상담 내용은 참고용이며 실제 보장 및 보험료는 심사 기준에 따라
              달라질 수 있습니다.
            </p>

            <button
              type="button"
              className="shrink-0 rounded-full bg-secondary/5 px-3 py-2 text-[12px] font-extrabold text-secondary hover:bg-secondary/10"
              onClick={handleCallPlanner}
            >
              설계사 연결 1566-3611
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
